import {
    computed,
    DestroyRef,
    inject,
    Injectable,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    EventTypes,
    OidcClientNotification,
    OidcSecurityService,
    PublicEventsService,
} from 'angular-auth-oidc-client';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs';
import {
    AGENT_SIGNALR_HUB,
    AGGREGATIONS_SIGNALR_HUB,
    CHAT_SIGNALR_HUB,
    EMAIL_SIGNALR_HUB,
    LIVE_SIGNALR_HUB,
    NOTIFICATION_SIGNALR_HUB,
} from '@dxp/core/signalr';
import { IRbacTeam } from '@dxp/core/models';
import { RbacApiService } from '@dxp/core/api';
import { LoggerService } from '../logger/logger.service';
import { ReloadService } from '../reload/reload.service';

function getEventTypeName(eventType: EventTypes): string {
    switch (eventType) {
        case EventTypes.CheckingAuth:
            return 'CheckingAuth';
        case EventTypes.CheckingAuthFinished:
            return 'CheckingAuthFinished';
        case EventTypes.CheckingAuthFinishedWithError:
            return 'CheckingAuthFinishedWithError';
        case EventTypes.ConfigLoadingFailed:
            return 'ConfigLoadingFailed';
        case EventTypes.CheckSessionReceived:
            return 'CheckSessionReceived';
        case EventTypes.UserDataChanged:
            return 'UserDataChanged';
        case EventTypes.NewAuthenticationResult:
            return 'NewAuthenticationResult';
        case EventTypes.TokenExpired:
            return 'TokenExpired';
        case EventTypes.IdTokenExpired:
            return 'IdTokenExpired';
        case EventTypes.SilentRenewStarted:
            return 'SilentRenewStarted';
        case EventTypes.SilentRenewFailed:
            return 'SilentRenewFailed';
        default:
            return 'Unknown Event';
    }
}

@Injectable({
    providedIn: 'root',
})
export class HubService {
    readonly #destroyRef = inject(DestroyRef);
    readonly #liveHubService = inject(LIVE_SIGNALR_HUB);
    readonly #aggregationsHubService = inject(AGGREGATIONS_SIGNALR_HUB);
    readonly #agentHubService = inject(AGENT_SIGNALR_HUB);
    readonly #notificationHubService = inject(NOTIFICATION_SIGNALR_HUB);
    readonly #chatHubService = inject(CHAT_SIGNALR_HUB);
    readonly #emailHubService = inject(EMAIL_SIGNALR_HUB);
    readonly #rbacApiService = inject(RbacApiService);
    readonly #oidcSecurityService = inject(OidcSecurityService);
    readonly #eventService = inject(PublicEventsService);
    readonly #logger = inject(LoggerService);
    readonly #reloadService = inject(ReloadService);

    readonly #queues = signal<string[]>([]);
    readonly #agents = signal<string[]>([]);

    readonly #lastAccessToken = signal<string>('');
    readonly #loading = signal<boolean>(true);

    readonly ready = computed<boolean>(() => !this.#loading());

    constructor() {
        this.#eventService
            .registerForEvents()
            .pipe(
                filter((notification: OidcClientNotification<any>) =>
                    [
                        EventTypes.NewAuthenticationResult,
                        EventTypes.TokenExpired,
                        EventTypes.IdTokenExpired,
                        EventTypes.SilentRenewStarted,
                        EventTypes.SilentRenewFailed,
                    ].includes(notification.type),
                ),
                switchMap((notification: OidcClientNotification<any>) =>
                    this.#oidcSecurityService.getAccessToken().pipe(
                        take(1),
                        map(currentToken => ({
                            eventType: notification.type,
                            currentToken,
                            eventValue: notification.value,
                        })),
                    ),
                ),
                tap(({ eventType, currentToken, eventValue }) => {
                    const eventTypeName = getEventTypeName(eventType);

                    this.#logger.debug(
                        `Authentication event: ${eventTypeName}`,
                        eventValue,
                    );

                    if (
                        currentToken &&
                        currentToken !== this.#lastAccessToken()
                    ) {
                        this.#lastAccessToken.set(currentToken);
                        this.onAccessTokenChange();
                        this.#logger.debug(
                            `Access token changed. Token updated at ${new Date().toISOString()}`,
                        );
                    } else {
                        this.#logger.debug(
                            `Access token unchanged. No update triggered.`,
                        );
                    }
                }),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();

        this.#initTeamLoader();
    }

    #initTeamLoader() {
        combineLatest(
            this.#oidcSecurityService.userData$,
            toObservable(this.#reloadService.refreshTime),
            toObservable(this.#reloadService.refreshTeams),
        )
            .pipe(
                map(([user, refresh, refreshTeams]) => ({
                    userId: user?.userData?.sub as unknown,
                    refresh,
                    refreshTeams,
                })),
                filter(
                    (
                        x,
                    ): x is {
                        userId: string;
                        refresh: number;
                        refreshTeams: number;
                    } => typeof x.userId === 'string' && x.userId.length > 0,
                ),
                distinctUntilChanged(
                    (a, b) =>
                        a.userId === b.userId &&
                        a.refresh === b.refresh &&
                        a.refreshTeams === b.refreshTeams,
                ),
                switchMap(({ userId }) => {
                    this.stopSignalRHubs();

                    return this.#rbacApiService.getTeams(userId).pipe(
                        catchError(error => {
                            this.#reloadService.error(
                                'HUB_SERVICE',
                                error.message,
                            );

                            return of([]);
                        }),
                    );
                }),
                tap(teams => {
                    this.#loading.set(false);
                    this.#initQueuesAndTeams(teams);
                    this.#startSignalRHubs();
                }),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();
    }

    #initQueuesAndTeams(teams: IRbacTeam[]) {
        const queues = teams
            ? [
                  ...new Set(
                      teams.reduce(
                          (accumulator, currentValue) => [
                              ...accumulator,
                              ...currentValue.queueIds,
                          ],
                          [] as string[],
                      ),
                  ),
              ]
            : [];

        const agents = teams
            ? [
                  ...new Set(
                      teams.reduce(
                          (accumulator, currentValue) => [
                              ...accumulator,
                              ...currentValue.memberIds,
                          ],
                          [] as string[],
                      ),
                  ),
              ]
            : [];

        this.#agents.set(agents);
        this.#queues.set(queues);
    }

    onAccessTokenChange() {
        this.#agentHubService.restart();
        this.#notificationHubService.restart();

        this.#liveHubService.restart({
            queueIds: this.#queues(),
        });

        this.#aggregationsHubService.restart({
            queueIds: this.#queues(),
            agentIds: this.#agents(),
        });

        this.#chatHubService.restart();
        this.#emailHubService.restart();
    }

    #startSignalRHubs() {
        this.#chatHubService.start();
        this.#emailHubService.start();
        this.#agentHubService.start();
        this.#notificationHubService.start();

        this.#liveHubService.start({
            queueIds: this.#queues(),
        });

        this.#aggregationsHubService.start({
            queueIds: this.#queues(),
            agentIds: this.#agents(),
        });
    }

    stopSignalRHubs() {
        this.#agentHubService.stop();
        this.#notificationHubService.stop();
        this.#aggregationsHubService.stop();
        this.#liveHubService.stop();
        this.#chatHubService.stop();
        this.#emailHubService.stop();
    }
}
